/**
 * RadNeo™ Design System — Color Tokens
 *
 * No hex literals should exist outside this file.
 * Use semantic names or import `semantic` / `primitive` objects.
 */

export const primitive = {
  mint: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0f766e',
  },
  charcoal: {
    50: '#f6f7f7',
    100: '#e3e6e5',
    200: '#c7ccc9',
    300: '#a3aba6',
    400: '#7a857e',
    500: '#5e6a62',
    600: '#4a544e',
    700: '#3e4742',
    800: '#263732',
    900: '#00190b',
  },
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
  },
  amber: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
  },
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#4E65EA',
    700: '#3A4FD4',
    800: '#1d4ed8',
  },
} as const;

export const semantic = {
  primary: {
    main: primitive.mint[500],
    light: primitive.mint[300],
    dark: primitive.mint[600],
    contrast: '#ffffff',
  },
  secondary: {
    main: primitive.charcoal[800],
    light: primitive.charcoal[500],
    dark: primitive.charcoal[900],
    contrast: '#ffffff',
  },
  success: {
    main: primitive.mint[500],
    light: primitive.mint[200],
    dark: primitive.mint[600],
    bg: primitive.mint[50],
  },
  warning: {
    main: primitive.amber[600],
    light: primitive.amber[500],
    dark: primitive.amber[700],
    bg: primitive.amber[50],
  },
  error: {
    main: primitive.red[600],
    light: primitive.red[500],
    dark: primitive.red[700],
    bg: primitive.red[50],
  },
  info: {
    main: primitive.blue[600],
    light: primitive.blue[400],
    dark: primitive.blue[700],
    bg: primitive.blue[50],
  },
  neutral: primitive.slate,
  surface: {
    default: '#ffffff',
    elevated: '#ffffff',
    overlay: 'rgba(0, 0, 0, 0.5)',
  },
  background: {
    default: primitive.slate[50],
    paper: '#ffffff',
    dark: primitive.charcoal[900],
  },
  text: {
    primary: primitive.slate[900],
    secondary: primitive.slate[500],
    disabled: primitive.slate[400],
    inverse: '#ffffff',
  },
  border: {
    default: primitive.slate[200],
    focus: primitive.mint[300],
    error: primitive.red[600],
  },
} as const;

/** Legacy alias for backward compat during migration. Prefer `semantic.primary.main`. */
export const BRAND_COLORS = {
  primary: semantic.primary.main,
  accent: semantic.secondary.main,
  primaryLight: semantic.primary.light,
  primaryDark: semantic.primary.dark,
  accentLight: primitive.charcoal[500],
  accentDark: primitive.charcoal[900],
  dark: semantic.background.dark,
  gradient: `linear-gradient(135deg, ${semantic.primary.main} 0%, ${primitive.charcoal[700]} 100%)`,
} as const;
